import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminOverview from "./AdminOverview";
import AdminUserManagement from "./AdminUserManagement";
import AdminAppointmentManagement from "./AdminAppointmentManagement";
import PracticeAreasManagement from "./PracticeAreasManagement";
import HearingManagement from "./HearingManagement";
import AdminUserComplaints from "./AdminUserComplaints";
import WebsiteSettings from "./WebsiteSettings";
import API_BASE_URL from "../config";

import {
  FiUsers,
  FiUserCheck,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiSearch,
  FiChevronRight,
  FiBarChart2,
  FiShield,
  FiMessageCircle,
} from "react-icons/fi";

import { MdDashboard, MdVerifiedUser, MdBlock, MdSecurity, MdCategory } from "react-icons/md";
import { HiUsers } from "react-icons/hi";

import "../styles/AdminDashboard.css";

export default function AdminDashboard() {
  const { logout, token } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState(() => {
    return sessionStorage.getItem("admin_active_section") || "dashboard";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [newComplaintsCount, setNewComplaintsCount] = useState(0);

  /* ======================
     FETCH ADMIN PROFILE
  ====================== */
  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Failed to load admin");

        const data = await res.json();
        setAdminData(data.user);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAdmin();
    fetchNewComplaintsCount();
  }, [token]);


  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const fetchNewComplaintsCount = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (data.success) {
        const newCount = data.data.filter(c => c.status === "NEW").length;
        setNewComplaintsCount(newCount);
      }
    } catch (err) {
      console.error("Failed to fetch complaints count", err);
    }
  };


  /* ======================
     ADMIN MENU
  ====================== */
  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard Overview",
      icon: <MdDashboard />
    },
    {
      id: "users",
      label: "User Management",
      icon: <HiUsers />
    },
    {
      id: "appointments",
      label: "Appointment Management",
      icon: <FiUsers />
    },
    {
      id: "practice",
      label: "PracticeAreas Management",
      icon: <MdCategory />
    },
    {
      id: "hearings",
      label: "Hearing Management",
      icon: <FiBell />
    },
    {
      id: "complaints",
      label: "User Complaints",
      icon: <FiMessageCircle />
    },
    {
      id: "system-config",
      label: "Website Configuration",
      icon: <FiSettings />
    },
    // {
    //   id: "reports",
    //   label: "Reports & Analytics",
    //   icon: <FiBarChart2 />
    // },
  ];



  return (
    <div className="luxury-admin-wrapper">
      {/* ======================
         MOBILE HEADER
      ====================== */}
      <header className="luxury-mobile-header">
        <button
          className="luxury-mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>

        <div className="luxury-mobile-brand">
          <div className="brand-icon-wrapper">
            <MdVerifiedUser className="brand-icon" size={22} />
          </div>
          <span>Admin Dashboard</span>
        </div>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="luxury-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ======================
         LUXURY SIDEBAR
      ====================== */}
      <aside className={`luxury-sidebar ${sidebarOpen ? "open" : ""}`}>
        {/* Brand */}
        <div className="luxury-sidebar-brand">
          <div className="luxury-brand-content">
            <div className="brand-icon-wrapper">
              <MdVerifiedUser className="brand-icon" size={28} />
            </div>
            <div>
              <h2>Admin Dashboard</h2>
            </div>
          </div>
        </div>

        {/* Admin Profile */}
        <div className="luxury-user-card">
          <div className="luxury-avatar-container">
            <div className="avatar-fallback">
              {adminData?.name?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div className="user-status-indicator"></div>
          </div>

          <div className="luxury-user-info">
            <h3 className="admin-name">
              {adminData?.name || "Administrator"}
            </h3>
            <p className="admin-email">{adminData?.email || "admin@system.com"}</p>

            <div className="user-role-tag">
              <FiShield size={12} />
              <span>Administrator</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="luxury-sidebar-nav">
          <div className="nav-header">
            <span className="nav-title">NAVIGATION</span>
          </div>

          <ul className="luxury-nav-menu">
            {menuItems.map(item => (
              <li
                key={item.id}
                className={`luxury-nav-item ${activeSection === item.id ? "active" : ""
                  }`}
                onClick={() => {
                  setActiveSection(item.id);
                  sessionStorage.setItem("admin_active_section", item.id);
                  setSidebarOpen(false);
                }}
              >
                <div className="nav-item-content">
                  <div className="nav-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="nav-text">
                    <span className="nav-label">{item.label}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ======================
         MAIN CONTENT (WHITE MODE)
      ====================== */}
      <main className="luxury-dashboard-main">
        <div className="luxury-content-wrapper">

          {/* ======================
       CONTENT HEADER (BREADCRUMB + BELL)
  ====================== */}
          <div className="luxury-breadcrumb-wrapper">

            {/* LEFT - Breadcrumb */}
            <div className="luxury-breadcrumb">
              <span className="breadcrumb-item">Admin Dashboard</span>
              <FiChevronRight size={12} />
              <span className="breadcrumb-item active">
                {menuItems.find(item => item.id === activeSection)?.label || "Dashboard"}
              </span>
            </div>

            {/* RIGHT - Bell Notification */}
            <div
              className="admin-bell-wrapper"
              onClick={() => {
                setActiveSection("complaints");
                sessionStorage.setItem("admin_active_section", "complaints");
              }}
              title="New User Complaints"
            >
              <FiBell size={22} />

              {newComplaintsCount > 0 && (
                <span className="admin-bell-badge">
                  {newComplaintsCount}
                </span>
              )}
            </div>

          </div>

          {/* ======================
       DYNAMIC CONTENT
  ====================== */}
          <div className="luxury-content-container">
            {activeSection === "dashboard" && <AdminOverview />}
            {activeSection === "users" && <AdminUserManagement />}
            {activeSection === "appointments" && <AdminAppointmentManagement />}
            {activeSection === "practice" && <PracticeAreasManagement />}
            {activeSection === "hearings" && <HearingManagement />}
            {activeSection === "complaints" && <AdminUserComplaints />}
            {activeSection === "system-config" && <WebsiteSettings />}

            {/* {activeSection === "reports" && (
      <div className="coming-soon">
        <FiBarChart2 size={64} />
        <h2>Reports & Analytics</h2>
      </div>
    )} */}
          </div>
        </div>

      </main>
    </div>
  );
}